import { formatDate } from "@/v1/components/pages/TaxDeclaration/utils";
import { DEFAULT_LOCALE } from "@/v1/constants";
import { formatAmount } from "@/v1/utils";
import { splitStreetForPreview } from "./formHelpers";
import { KAPG_COUNTRY_OPTIONS } from "./kapgCountryOptions";
import { getShareTotal } from "./kapgGates";
import {
  TaxRegistrationAddressType,
  TaxRegistrationEstimatedBalanceType,
  TaxRegistrationShareholderType,
  TaxRegistrationTaxCalculationMethod,
  type AddressAnswers,
  type TaxRegistrationAttachment,
  type TaxRegistrationKapgAddress,
  type TaxRegistrationQuestions,
  type TaxRegistrationRepresentative,
  type TaxRegistrationShareholder,
} from "./types";

/**
 * The KapG questionnaire as ELSTER files it: FsE_KapG sections, in their own
 * order, each row carrying the Zeile it is transmitted in.
 *
 * A mapper of its own rather than a branch in the preview modal, because the
 * two Datenarten share nothing: the same number means a different field in
 * FsE_KapG than in the Einzelunternehmer form, which is why a KapG record used
 * to be shown no preview at all (SMT-1294, corrected by SMT-1335).
 *
 * Two rules hold everywhere below:
 *  - an unanswered optional field produces no row. An empty row would read as
 *    "answered with nothing", and in a tax form that is a different statement;
 *  - the labels come from `preview.kapg.*`, not from `kapg.fields.*`. The form
 *    labels carry their Zeile inside the text ("Amtsgericht (Zeile 56)") and
 *    the preview already has a column for it. Stripping that suffix would have
 *    to survive both catalogues, where English writes it as
 *    "Local court (Amtsgericht, line 56)"; a second, short label set is the
 *    honest way to say it once.
 */
export interface KapgPreviewRow {
  /** The FsE_KapG line, shown in its own column. "67a" is a real one. */
  zeile: string;
  label: string;
  value: string;
  /**
   * The repeating block or the address this row belongs to, e.g.
   * "Gesetzlicher Vertreter 2" or "Ort der Geschäftsleitung". Without it the
   * six Anteilseigner and the two company addresses repeat the same labels
   * with nothing to tell them apart.
   */
  group?: string;
}

export interface KapgPreviewSection {
  title: string;
  rows: KapgPreviewRow[];
}

export type KapgPreviewTranslate = (
  key: string,
  options?: Record<string, unknown>
) => string;

interface RowInput {
  zeile: string;
  label: string;
  value?: string | null;
}

interface AddressZeilen {
  street: string;
  addressExtra?: string;
  location: string;
}

const COUNTRY_LABEL_BY_CODE = new Map(
  KAPG_COUNTRY_OPTIONS.map(({ value, label }) => [value, label])
);

const formatPercentage = (value: number): string =>
  `${new Intl.NumberFormat(DEFAULT_LOCALE, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(value)} %`;

const formatCount = (value?: number): string | undefined =>
  typeof value === "number"
    ? new Intl.NumberFormat(DEFAULT_LOCALE).format(value)
    : undefined;

const formatEuro = (value?: number): string | undefined =>
  typeof value === "number" ? formatAmount(value) : undefined;

/** Rows without an answer never reach the table. */
const collectRows = (rows: RowInput[], group?: string): KapgPreviewRow[] =>
  rows
    .filter(
      ({ value }) => value !== undefined && value !== null && value !== ""
    )
    .map(({ zeile, label, value }) => ({
      zeile,
      label,
      value: String(value),
      ...(group ? { group } : {}),
    }));

const buildSection = (
  title: string,
  rows: KapgPreviewRow[]
): KapgPreviewSection[] => (rows.length ? [{ title, rows }] : []);

/**
 * A domestic address (Geschäftsanschrift, Ort der Geschäftsleitung) or the
 * two-shaped address of a person (Zeilen 28 to 30 and 74 to 76). A foreign one
 * carries `foreignZip` and a Staat and must not carry a German PLZ, which is
 * how it is stored and how it is shown.
 */
const buildAddressRows = (
  address: (AddressAnswers & Partial<TaxRegistrationKapgAddress>) | undefined,
  zeilen: AddressZeilen,
  translate: KapgPreviewTranslate
): RowInput[] => {
  if (!address) return [];

  const isForeign = address.type === TaxRegistrationAddressType.FOREIGN;
  const { street, houseNumber } = splitStreetForPreview(address.street);

  return [
    { zeile: zeilen.street, label: translate("fields.street"), value: street },
    {
      zeile: zeilen.street,
      label: translate("fields.houseNumber"),
      value: houseNumber,
    },
    ...(zeilen.addressExtra
      ? [
          {
            zeile: zeilen.addressExtra,
            label: translate("kapg.fields.addressExtra"),
            value: address.addressExtra,
          },
        ]
      : []),
    {
      zeile: zeilen.location,
      label: isForeign
        ? translate("kapg.fields.foreignZip")
        : translate("fields.zip"),
      value: isForeign ? address.foreignZip : address.zip,
    },
    {
      zeile: zeilen.location,
      label: translate("fields.city"),
      value: address.city,
    },
    ...(isForeign
      ? [
          {
            zeile: zeilen.location,
            label: translate("kapg.fields.country"),
            value: address.country
              ? COUNTRY_LABEL_BY_CODE.get(address.country) ?? address.country
              : undefined,
          },
        ]
      : []),
  ];
};

const buildGeneralSection = (
  questions: TaxRegistrationQuestions,
  translate: KapgPreviewTranslate,
  yesNo: (value?: boolean) => string | undefined
): KapgPreviewSection[] => {
  const company = questions.company;

  return buildSection(translate("kapg.sections.general"), [
    ...collectRows([
      {
        zeile: "2",
        label: translate("preview.kapg.fields.companyName"),
        value: company?.name,
      },
    ]),
    ...collectRows(
      buildAddressRows(
        company?.businessAddress,
        { street: "4", location: "6" },
        translate
      ),
      translate("preview.kapg.groups.businessAddress")
    ),
    ...collectRows([
      {
        zeile: "13",
        label: translate("preview.kapg.fields.activityDescription"),
        value: company?.activityDescription,
      },
      {
        zeile: "14",
        label: translate("preview.kapg.fields.vermoegensverwaltend"),
        value: yesNo(company?.vermoegensverwaltend),
      },
    ]),
    ...collectRows(
      buildAddressRows(
        company?.managementAddress,
        { street: "8", location: "10" },
        translate
      ),
      translate("preview.kapg.groups.managementAddress")
    ),
  ]);
};

const SALUTATION_OPTION_KEY: Record<string, string> = {
  MS: "options.salutationMs",
  MR: "options.salutationMr",
  NOT_SPECIFIED: "options.notSpecified",
};

const buildRepresentativeRows = (
  representative: TaxRegistrationRepresentative,
  index: number,
  translate: KapgPreviewTranslate
): KapgPreviewRow[] =>
  collectRows(
    [
      {
        zeile: "25",
        label: translate("fields.salutation"),
        value: representative.salutation
          ? translate(SALUTATION_OPTION_KEY[representative.salutation])
          : undefined,
      },
      {
        zeile: "25",
        label: translate("fields.firstName"),
        value: representative.firstName,
      },
      {
        zeile: "25",
        label: translate("fields.lastName"),
        value: representative.lastName,
      },
      {
        zeile: "26",
        label: translate("fields.dateOfBirth"),
        value: formatDate(representative.dateOfBirth),
      },
      ...buildAddressRows(
        representative.address,
        { street: "28", addressExtra: "29", location: "30" },
        translate
      ),
    ],
    translate("kapg.labels.representative", { index: index + 1 })
  );

const buildNotarizationSection = (
  questions: TaxRegistrationQuestions,
  translate: KapgPreviewTranslate,
  yesNo: (value?: boolean) => string | undefined
): KapgPreviewSection[] => {
  const notarization = questions.company?.notarization;
  const register = notarization?.commercialRegister;

  return buildSection(
    translate("kapg.sections.notarization"),
    collectRows([
      {
        zeile: "54",
        label: translate("preview.kapg.fields.contractDate"),
        value: formatDate(notarization?.contractDate),
      },
      {
        zeile: "55",
        label: translate("preview.kapg.fields.applicationFiled"),
        value: yesNo(register?.applicationFiled),
      },
      {
        zeile: "55",
        label: translate("preview.kapg.fields.applicationDate"),
        value: formatDate(register?.applicationDate),
      },
      {
        zeile: "55",
        label: translate("preview.kapg.fields.registered"),
        value: yesNo(register?.registered),
      },
      {
        zeile: "55",
        label: translate("preview.kapg.fields.registrationDate"),
        value: formatDate(register?.registrationDate),
      },
      {
        zeile: "56",
        label: translate("preview.kapg.fields.court"),
        value: register?.court,
      },
      {
        zeile: "57",
        label: translate("preview.kapg.fields.registerType"),
        value: register?.registerType,
      },
      {
        zeile: "57",
        label: translate("preview.kapg.fields.registerNumber"),
        value: register?.registerNumber,
      },
    ])
  );
};

const buildShareholderRows = (
  shareholder: TaxRegistrationShareholder,
  index: number,
  translate: KapgPreviewTranslate
): KapgPreviewRow[] => {
  const isLegalEntity =
    shareholder.type === TaxRegistrationShareholderType.LEGAL;

  return collectRows(
    [
      {
        zeile: "68",
        label: translate("preview.kapg.fields.subscriberNumber"),
        value: shareholder.subscriberNumber,
      },
      ...(isLegalEntity
        ? [
            {
              zeile: "72",
              label: translate("preview.kapg.fields.shareholderCompanyName"),
              value: shareholder.companyName,
            },
            {
              zeile: "73",
              label: translate("preview.kapg.fields.businessActivity"),
              value: shareholder.businessActivity,
            },
          ]
        : [
            {
              zeile: "69",
              label: translate("fields.firstName"),
              value: shareholder.firstName,
            },
            {
              zeile: "69",
              label: translate("fields.lastName"),
              value: shareholder.lastName,
            },
            {
              zeile: "70",
              label: translate("fields.dateOfBirth"),
              value: formatDate(shareholder.dateOfBirth),
            },
          ]),
      ...buildAddressRows(
        shareholder.address,
        { street: "74", addressExtra: "75", location: "76" },
        translate
      ),
      {
        zeile: "78",
        label: translate("preview.kapg.fields.nominal"),
        value: formatEuro(shareholder.nominal),
      },
      {
        zeile: "78",
        label: translate("preview.kapg.fields.percentage"),
        value:
          typeof shareholder.percentage === "number"
            ? formatPercentage(shareholder.percentage)
            : undefined,
      },
    ],
    translate("kapg.labels.shareholder", { index: index + 1 })
  );
};

const buildVatSection = (
  questions: TaxRegistrationQuestions,
  translate: KapgPreviewTranslate,
  yesNo: (value?: boolean) => string | undefined
): KapgPreviewSection[] => {
  // A vermögensverwaltende Gesellschaft answers no VAT question at all, so the
  // section is absent rather than empty (the form hides it the same way).
  if (questions.company?.vermoegensverwaltend) return [];

  const vat = questions.vat;
  const isCashAccounting =
    vat?.taxCalculationMethod === TaxRegistrationTaxCalculationMethod.CASH;
  /*
   * Zeilen 180 and 181 are two mutually exclusive ELSTER checkboxes, stored as
   * the single `smallBusiness` radio. So "no small-business scheme" is filed as
   * a tick in 181, not as a "Nein" in 180, and that is what is shown.
   */
  const claimsSmallBusiness = vat?.smallBusiness === true;
  const waivesSmallBusiness =
    vat?.smallBusinessWaiver ?? vat?.smallBusiness === false;

  return buildSection(
    translate("sections.vat"),
    collectRows([
      {
        zeile: "179",
        label: translate("preview.kapg.fields.expectedTurnover"),
        value: formatEuro(vat?.expectedTurnover),
      },
      {
        zeile: "179",
        label: translate("preview.kapg.fields.expectedTurnoverFollowingYear"),
        value: formatEuro(vat?.expectedTurnoverFollowingYear),
      },
      {
        zeile: "180",
        label: translate("preview.kapg.fields.smallBusiness"),
        value: claimsSmallBusiness ? translate("common.yes") : undefined,
      },
      {
        zeile: "181",
        label: translate("preview.kapg.fields.smallBusinessWaiver"),
        value: waivesSmallBusiness ? translate("common.yes") : undefined,
      },
      {
        zeile: "182",
        label: translate("preview.kapg.fields.estimatedBalanceType"),
        value:
          vat?.estimatedBalanceType ===
          TaxRegistrationEstimatedBalanceType.PAYMENT
            ? translate("options.vatPayment")
            : vat?.estimatedBalanceType ===
              TaxRegistrationEstimatedBalanceType.REFUND
            ? translate("options.vatRefund")
            : undefined,
      },
      {
        zeile: "182",
        label: translate("preview.kapg.fields.estimatedBalanceAmount"),
        value: formatEuro(vat?.estimatedBalanceAmount),
      },
      {
        zeile: "183",
        label: translate("preview.kapg.fields.monthlyFilingPeriod"),
        value: yesNo(vat?.monthlyFilingPeriod),
      },
      {
        zeile: "194",
        label: translate("preview.kapg.fields.taxCalculationMethod"),
        value: isCashAccounting
          ? translate("options.cash")
          : vat?.taxCalculationMethod ===
            TaxRegistrationTaxCalculationMethod.ACCRUAL
          ? translate("options.accrual")
          : undefined,
      },
      // Zeilen 195 and 196 only exist as reasons for the Ist-Versteuerung, so
      // a leftover tick under Soll-Versteuerung is not filed and not shown.
      {
        zeile: "195",
        label: translate("preview.kapg.fields.cashAccountingTurnoverLimit"),
        value:
          isCashAccounting && vat?.cashAccountingTurnoverLimit
            ? translate("common.yes")
            : undefined,
      },
      {
        zeile: "196",
        label: translate("preview.kapg.fields.cashAccountingSection148Ao"),
        value:
          isCashAccounting && vat?.cashAccountingSection148Ao
            ? translate("common.yes")
            : undefined,
      },
      {
        zeile: "197",
        label: translate("preview.kapg.fields.vatId"),
        value: translate("common.yes"),
      },
    ])
  );
};

export const getKapgPreviewSections = (
  questions: TaxRegistrationQuestions,
  translate: KapgPreviewTranslate,
  attachments: TaxRegistrationAttachment[] = []
): KapgPreviewSection[] => {
  const yesNo = (value?: boolean) =>
    typeof value === "boolean"
      ? translate(value ? "common.yes" : "common.no")
      : undefined;
  const company = questions.company;
  const representatives = questions.representatives ?? [];
  const shareholders = questions.shareholders ?? [];
  const payroll = questions.payroll;
  const profit = questions.profit;

  return [
    ...buildGeneralSection(questions, translate, yesNo),
    ...buildSection(
      translate("kapg.sections.representatives"),
      representatives.flatMap((representative, index) =>
        buildRepresentativeRows(representative, index, translate)
      )
    ),
    ...buildNotarizationSection(questions, translate, yesNo),
    ...buildSection(
      translate("kapg.sections.legalForm"),
      collectRows([
        {
          zeile: "63",
          label: translate("preview.kapg.fields.legalForm"),
          value: company?.legalForm
            ? translate(`preview.kapg.options.legalForm.${company.legalForm}`)
            : undefined,
        },
        {
          zeile: "64",
          label: translate("preview.kapg.fields.startDate"),
          value: formatDate(company?.startDate),
        },
        // Fixed by the template: the questionnaire never offers a deviating
        // Wirtschaftsjahr, so ELSTER always receives "Nein".
        {
          zeile: "65",
          label: translate("preview.kapg.fields.fiscalYearDeviating"),
          value: translate("common.no"),
        },
      ])
    ),
    ...buildSection(
      translate("kapg.sections.capital"),
      collectRows([
        {
          zeile: "66",
          label: translate("preview.kapg.fields.shareCapital"),
          value: formatEuro(company?.shareCapital),
        },
        {
          zeile: "67",
          label: translate("preview.kapg.fields.paidInCapital"),
          value: formatEuro(company?.paidInCapital),
        },
        // Zeile 67a is computed for the operator and never transmitted: the
        // ELSTER template adds the shareholdings up itself.
        {
          zeile: "67a",
          label: translate("preview.kapg.fields.shareTotal"),
          value: shareholders.length
            ? formatPercentage(getShareTotal(shareholders))
            : undefined,
        },
      ])
    ),
    ...buildSection(
      translate("kapg.sections.shareholders"),
      shareholders.flatMap((shareholder, index) =>
        buildShareholderRows(shareholder, index, translate)
      )
    ),
    ...buildSection(
      translate("kapg.sections.fixedAnswers"),
      collectRows([
        {
          zeile: "90",
          label: translate("preview.kapg.fields.trust"),
          value: translate("common.no"),
        },
        {
          zeile: "91",
          label: translate("preview.kapg.fields.cashFoundation"),
          value: translate("kapg.readOnly.cashFoundationValue"),
        },
      ])
    ),
    ...buildSection(
      translate("kapg.sections.profit"),
      collectRows([
        {
          zeile: "165",
          label: translate("preview.kapg.fields.netIncome"),
          value: formatEuro(profit?.netIncome),
        },
        {
          zeile: "165",
          label: translate("preview.kapg.fields.netIncomeFollowingYear"),
          value: formatEuro(profit?.netIncomeFollowingYear),
        },
      ])
    ),
    ...buildSection(
      translate("kapg.sections.payroll"),
      collectRows([
        {
          zeile: "169",
          label: translate("preview.kapg.fields.employeesTotal"),
          value: formatCount(payroll?.employeesTotal),
        },
        {
          zeile: "170",
          label: translate("preview.kapg.fields.employeesShareholders"),
          value: formatCount(payroll?.employeesShareholders),
        },
        {
          zeile: "171",
          label: translate("preview.kapg.fields.employeesMinorEmployment"),
          value: formatCount(payroll?.employeesMinorEmployment),
        },
        {
          zeile: "172",
          label: translate("preview.kapg.fields.paymentStartDate"),
          value: formatDate(payroll?.paymentStartDate),
        },
        {
          zeile: "173",
          label: translate("preview.kapg.fields.expectedWageTax"),
          value: formatEuro(payroll?.expectedWageTax),
        },
      ])
    ),
    ...buildVatSection(questions, translate, yesNo),
    ...buildSection(
      translate("kapg.sections.attachments"),
      collectRows([
        {
          zeile: "227",
          label: translate("preview.kapg.fields.articlesOfAssociation"),
          value: translate("common.yes"),
        },
        ...attachments.map((attachment, index) => ({
          zeile: "227",
          label: translate("preview.kapg.fields.attachment", {
            index: index + 1,
          }),
          value: attachment.fileName,
        })),
      ])
    ),
  ];
};
